/** CMS › Açılır pencereler: "kime, nerede, ne sıklıkla" kararı.
 *
 * Pencereler `/settings` yanıtındaki `cms_popups` listesidir (bkz.
 * backend/services/cms_content.py). O yanıt nginx'te önbelleklenir ve herkese
 * aynı gider; kitle kararı bu yüzden **burada** verilir ve saf fonksiyondur.
 *
 * İki tür vardır: `runtime` (Çalışma zamanı) sitede gezinene gösterilir,
 * `register` (Kayıt) yalnızca kaydı yeni tamamlanan oyuncuya — kayıt anında
 * sekmeye bırakılan işaretle (`markJustRegistered`).
 */
import { pageKeyFromPath } from './pageKey';
import { turkeyDay } from './welcomePopup';

export const CMS_POPUP_STORAGE_KEY = 'app:cms-popups-v1';
export const CMS_POPUP_SESSION_KEY = 'app:cms-popups-session-v1';
export const JUST_REGISTERED_KEY = 'app:just-registered-v1';
export const MOBILE_QUERY = '(max-width: 1023px)';
const NEW_PROFILE_DAYS = 7;

const storage = (name) => {
  try { return typeof window !== 'undefined' ? window[name] : null; } catch { return null; }
};
const readJson = (store, key) => {
  try { const raw = store?.getItem(key); return raw ? JSON.parse(raw) : {}; } catch { return {}; }
};
const writeJson = (store, key, value) => {
  try { store?.setItem(key, JSON.stringify(value)); } catch { /* gizli sekme */ }
};

export const markJustRegistered = () => {
  try { storage('sessionStorage')?.setItem(JUST_REGISTERED_KEY, '1'); } catch { /* yok sayılır */ }
};
export const readJustRegistered = () => {
  try { return storage('sessionStorage')?.getItem(JUST_REGISTERED_KEY) === '1'; } catch { return false; }
};
export const clearJustRegistered = () => {
  try { storage('sessionStorage')?.removeItem(JUST_REGISTERED_KEY); } catch { /* yok sayılır */ }
};

export const isMobileViewport = () => {
  try { return typeof window !== 'undefined' && Boolean(window.matchMedia?.(MOBILE_QUERY).matches); } catch { return false; }
};

export const readPopupSeen = () => ({
  seen: readJson(storage('localStorage'), CMS_POPUP_STORAGE_KEY),
  sessionSeen: readJson(storage('sessionStorage'), CMS_POPUP_SESSION_KEY),
});

export const markPopupSeen = (popup, now = Date.now()) => {
  const local = readJson(storage('localStorage'), CMS_POPUP_STORAGE_KEY);
  local[popup.id] = { version: Number(popup.version) || 0, day: turkeyDay(now) };
  writeJson(storage('localStorage'), CMS_POPUP_STORAGE_KEY, local);
  const session = readJson(storage('sessionStorage'), CMS_POPUP_SESSION_KEY);
  session[popup.id] = { version: Number(popup.version) || 0 };
  writeJson(storage('sessionStorage'), CMS_POPUP_SESSION_KEY, session);
};

/** Sitenin sayfa adı → paneldeki "Sayfalar" seçimi. */
const PAGE_MAP = {
  home: 'home', slot: 'casino', crash: 'casino', game: 'casino', 'live-casino': 'live-casino',
  sports: 'sports', promotions: 'promotions', 'promo-code': 'promotions', account: 'account',
};

const ageOf = (birthDate, now) => {
  const born = new Date(birthDate);
  if (!birthDate || Number.isNaN(born.getTime())) return null;
  const today = new Date(now);
  let age = today.getFullYear() - born.getFullYear();
  const beforeBirthday = today.getMonth() < born.getMonth()
    || (today.getMonth() === born.getMonth() && today.getDate() < born.getDate());
  if (beforeBirthday) age -= 1;
  return age;
};

/** Pencere bu ziyaretçiye, bu sayfada, şimdi gösterilsin mi? */
export const popupMatches = (popup, {
  user = null, pathname = '/', mobile = false, justRegistered = false,
  seen = {}, sessionSeen = {}, now = Date.now(),
} = {}) => {
  if (!popup || typeof popup !== 'object') return false;
  if (!(popup.title || popup.body || popup.image_url)) return false;
  const path = String(pathname || '/');
  if (path.startsWith('/admin')) return false;
  if (popup.subtype === 'register' && !(justRegistered && user)) return false;
  const platforms = popup.platform?.length ? popup.platform : ['web', 'mobile'];
  if (!platforms.includes(mobile ? 'mobile' : 'web')) return false;
  const showFor = popup.show_for || 'all';
  if (showFor === 'logged_in' && !user) return false;
  if (showFor === 'logged_out' && user) return false;
  const pages = popup.pages?.length ? popup.pages : ['all'];
  if (!pages.includes('all')) {
    // Seçim ya geniş bölüm (casino → slot/crash/oyun), ya rota adı (pageKey),
    // ya da tek bir içerik sayfasıdır (`page:<adres>` → /sayfa/<adres>).
    const key = pageKeyFromPath(path);
    const slug = key === 'page' ? decodeURIComponent(path.split('/')[2] || '') : '';
    const hit = pages.includes(PAGE_MAP[key]) || pages.includes(key) || (slug && pages.includes(`page:${slug}`));
    if (!hit) return false;
  }
  if (popup.end_at && new Date(popup.end_at).getTime() < now) return false;
  const country = String(user?.country || '').toLocaleLowerCase('tr');
  if (country) {
    const lower = (list) => (list || []).map((c) => String(c).toLocaleLowerCase('tr'));
    if (lower(popup.excluded_countries).includes(country)) return false;
    if (popup.included_countries?.length && !lower(popup.included_countries).includes(country)) return false;
  }
  if (user) {
    const created = user.registered_at ? new Date(user.registered_at).getTime() : 0;
    const isNew = created > 0 && now - created < NEW_PROFILE_DAYS * 86400000;
    if (popup.profile_type === 'new' && !isNew) return false;
    if (popup.profile_type === 'existing' && isNew) return false;
    if (popup.deposit_status === 'deposited' && !user.has_deposit) return false;
    if (popup.deposit_status === 'not_deposited' && user.has_deposit) return false;
    if (popup.profile_status === 'verified' && !user.kyc_verified) return false;
    if (popup.profile_status === 'unverified' && user.kyc_verified) return false;
    if (popup.age_action && popup.age_action !== 'none') {
      const age = ageOf(user.birth_date, now);
      const min = Number(popup.age_min) || 0;
      const max = Number(popup.age_max) || 0;
      const inRange = age !== null && age >= min && (!max || age <= max);
      if (popup.age_action === 'show' && !inRange) return false;
      if (popup.age_action === 'hide' && inRange) return false;
    }
  } else if (['profile_type', 'deposit_status', 'profile_status'].some((k) => popup[k] && popup[k] !== 'all')) {
    return false;
  }
  const version = Number(popup.version) || 0;
  const local = seen?.[popup.id];
  const session = sessionSeen?.[popup.id];
  switch (popup.repeat || 'once') {
    case 'always': return true;
    case 'session': return !(session && Number(session.version) === version);
    case 'daily': return !(local && Number(local.version) === version && local.day === turkeyDay(now));
    case 'once':
    default: return !(local && Number(local.version) === version);
  }
};

/** Gösterilecek ilk pencere (panel sırası korunur). */
export const pickPopup = (popups, context) => (
  (Array.isArray(popups) ? popups : []).find((popup) => popupMatches(popup, context)) || null
);

// Aynı anda üst üste gösterilen en fazla pencere. Panelde sınır yoktur; bu
// yalnız oyuncunun art arda kapatacağı pencere sayısını makul tutar.
export const POPUP_STACK_MAX = 5;

/**
 * Uyan bütün pencereler, panel sırasıyla (26.10.2026, sistem sahibi): kaydırmalı
 * (slider) değil üst üste gösterilir; ilki öndedir, kapatılınca sıradaki öne gelir.
 */
export const pickPopups = (popups, context, max = POPUP_STACK_MAX) => (
  (Array.isArray(popups) ? popups : []).filter((popup) => popupMatches(popup, context)).slice(0, max)
);

/** Header üstü kayan bildirimler: platform ve tarih süzgeci. */
export const liveNotifications = (notes, { mobile = false, user = null, now = Date.now() } = {}) => (
  (Array.isArray(notes) ? notes : []).filter((note) => {
    const platforms = note.platform?.length ? note.platform : ['web', 'mobile'];
    if (!platforms.includes(mobile ? 'mobile' : 'web')) return false;
    if (note.end_at && new Date(note.end_at).getTime() < now) return false;
    if (note.show_for === 'logged_in' && !user) return false;
    if (note.show_for === 'logged_out' && user) return false;
    return Boolean(note.title || note.body);
  })
);

/** Bildirimin düz metni (gövde HTML olabilir; kayan bantta düz metin çizilir). */
export const notificationText = (note) => {
  const raw = note?.body || note?.title || '';
  if (!/<[a-z]/i.test(raw)) return raw;
  // DOMParser betik ve olay işleyicisi çalıştırmaz (innerHTML'in aksine).
  if (typeof DOMParser === 'undefined') return raw.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();
  const doc = new DOMParser().parseFromString(raw, 'text/html');
  return (doc.body?.textContent || '').replace(/\s+/g, ' ').trim();
};

/** Promosyon kartının CMS kuralları (platform, kitle). Üye bilgisi kartın
 * çizildiği an elde olmayabilir; kitle kuralı yalnızca platformla birlikte
 * oturum bilgisini kullanır (`setPromotionViewer`). */
let promotionViewer = { user: null };
export const setPromotionViewer = (user) => { promotionViewer = { user: user || null }; };
export const promotionAllowed = (meta, { mobile = isMobileViewport(), user = promotionViewer.user } = {}) => {
  if (!meta) return true;
  const platforms = meta.platform?.length ? meta.platform : ['web', 'mobile'];
  if (!platforms.includes(mobile ? 'mobile' : 'web')) return false;
  if (meta.show_for === 'logged_in' && !user) return false;
  if (meta.show_for === 'logged_out' && user) return false;
  return true;
};

/**
 * CMS penceresini eski karşılama penceresi biçimine çevirir (Betrun, 21.10.2026).
 *
 * `cms-popup-from-welcome` göçü eski pencerenin düğmesini `link` +
 * `additional.button_label` olarak taşır; CMS kartı düğme çizmez. Canlı
 * Betrun'un özel JS'i düğmenin yanına "Daha sonra" ve avantaj satırını
 * `.welcome-popup-content` / `.welcome-popup-button` üzerine ekler — düğme
 * yoksa pencere yalnız görsel kalıyordu. Düğme yazısı ve bağlantısı olan,
 * gövdesi boş pencere eski kartla çizilir; HTML gövdeli pencere CMS kartında
 * (çerçevede) kalır. Uymuyorsa `null`.
 */
export const cmsAsWelcome = (popup) => {
  if (!popup || popup.body) return null;
  const extras = Object.fromEntries((popup.additional || []).map((entry) => [entry.key, entry.value]));
  const label = String(extras.button_label || '').trim();
  const link = String(popup.link || '').trim();
  if (!label || !link) return null;
  return {
    image_url: popup.image_url || '', title: popup.title || '', body: '',
    button_label: label, button_url: link,
  };
};
